import { useCallback, useEffect, useMemo, useState } from 'react';
import { toApiError } from '../../api/errors';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import type { CollectionDef } from './collections';
import { AddRowForm } from './AddRowForm';
import { hasPendingOrder, moveCode, orderChanges } from './reorder';
import {
  createVocabRow,
  fetchCollection,
  refusalReason,
  updateVocabRow,
  writeRouteMissing,
  type CreateVocabBody,
  type UpdateVocabBody,
  type VocabRow,
} from './settings-api';
import { laneUsage, unavailableUsage, useUsageCounts, type Usage } from './usage';
import { VocabRowItem } from './VocabRowItem';
import { canWrite, disabledReason, type WriteGate } from './WriteGate';

/**
 * One vocabulary, as a reorderable list of rows.
 *
 * 🔴 THE SHAPE IS THE SAME FOR ALL SIX because the question is the same for all six: what are the
 * permitted values, in what order, which are retired, and what breaks if I retire this one.
 *
 * ⛔ NO DELETE CONTROL EXISTS ANYWHERE IN THIS SUBTREE. Not hidden, not disabled — absent. See
 * `VocabRowItem`.
 */
export function VocabularyPanel({
  def,
  gate,
  lanes,
  onCollectionChanged,
}: {
  def: CollectionDef;
  gate: WriteGate;
  /** Only the statuses panel uses these — for the lane picker on the add form and the usage count. */
  lanes: readonly VocabRow[];
  onCollectionChanged: () => void;
}) {
  const collection = def.name as Exclude<CollectionDef['name'], 'modules'>;
  const rows = useResource<VocabRow[]>((signal) => fetchCollection(collection, signal), [collection]);

  /**
   * ⭐ THE STATUSES ARE FETCHED FOR THE LANES PANEL TOO. A lane's usage is "how many statuses sit in
   * it" — the thing that actually breaks when you retire one. `GET /tickets` has no lane filter, so
   * the alternative is not a different number, it is no number.
   */
  const statuses = useResource<VocabRow[]>(
    (signal) => fetchCollection('statuses', signal),
    ['statuses'],
    { enabled: def.name === 'lanes' },
  );

  const serverRows = useMemo(() => rows.data ?? [], [rows.data]);
  const [pending, setPending] = useState<VocabRow[] | null>(null);
  const shown = pending ?? serverRows;

  // A fresh server answer discards a local drag — ⛔ merging the two would show an order that is
  // neither what the server holds nor what the user asked for.
  useEffect(() => setPending(null), [serverRows]);

  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [addError, setAddError] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragCode, setDragCode] = useState<string | null>(null);

  const usageCounts = useUsageCounts(def, serverRows);

  const usageFor = useCallback(
    (row: VocabRow): Usage => {
      if (def.name === 'lanes') {
        // ⚠️ Only once the statuses have actually loaded. Counting an empty list would print
        // "0 statuses" for every lane, which reads as "safe to retire" and is a claim about a request
        // that has not answered yet.
        if (statuses.isLoading) return { kind: 'loading' };
        if (!statuses.data) return { kind: 'failed', why: 'Could not load the statuses to count them.' };
        return laneUsage(statuses.data, row.code);
      }
      if (!def.usageFilter) return unavailableUsage(def.name);
      return usageCounts.get(row.code) ?? { kind: 'loading' };
    },
    [def, statuses.isLoading, statuses.data, usageCounts],
  );

  const recordFailure = useCallback((code: string, err: unknown) => {
    const refusal = refusalReason(err);
    if (refusal) {
      setRowErrors((previous) => ({ ...previous, [code]: refusal }));
      return;
    }
    if (writeRouteMissing(err)) {
      setRowErrors((previous) => ({
        ...previous,
        [code]: 'The API answered "no such route" — the settings write endpoints are not deployed.',
      }));
      return;
    }
    const apiError = toApiError(err, `/settings/${collection}`);
    setRowErrors((previous) => ({
      ...previous,
      [code]: `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`,
    }));
  }, [collection]);

  const runWrite = useCallback(
    async (code: string, work: () => Promise<void>) => {
      setBusyCode(code);
      setRowErrors((previous) => {
        const next = { ...previous };
        delete next[code];
        return next;
      });
      try {
        await work();
        rows.reload();
        onCollectionChanged();
      } catch (err) {
        recordFailure(code, err);
      } finally {
        setBusyCode(null);
      }
    },
    [recordFailure, rows, onCollectionChanged],
  );

  const saveOrder = useCallback(async () => {
    if (!pending) return;
    const changes = orderChanges(serverRows, pending);
    setSavingOrder(true);
    try {
      // ⚠️ Sequential, not Promise.all: a partial failure of a parallel renumber leaves the list in
      // an order nobody chose, and the first failure here stops the rest.
      for (const change of changes) {
        await updateVocabRow(collection, change.code, { sort_order: change.sort_order });
      }
      setPending(null);
      rows.reload();
      onCollectionChanged();
    } catch (err) {
      recordFailure(changes[0]?.code ?? '', err);
    } finally {
      setSavingOrder(false);
    }
  }, [pending, serverRows, collection, rows, recordFailure, onCollectionChanged]);

  const editable = canWrite(gate);

  return (
    <section aria-label={def.title} className="min-w-0">
      <header className="px-3 py-3 sm:px-4">
        <h2 className="text-base font-semibold">{def.title}</h2>
        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">{def.purpose}</p>
      </header>

      <div className="space-y-3 px-3 pb-3 sm:px-4">
        {rows.error && !rows.isStale ? <ErrorNotice error={rows.error} onRetry={rows.reload} /> : null}
        {rows.isStale && rows.error ? (
          <StaleBanner error={rows.error} loadedAt={rows.loadedAt} onRetry={rows.reload} />
        ) : null}

        {/*
          🔴 THE RULE, STATED WHERE THE TOGGLE IS. Not an apology for a missing button — an
          explanation of why retirement is the only operation there is.
        */}
        <Notice tone="info" title="Deactivate, never delete">
          <p>
            A value referenced by a ticket or by <code>ticket_status_history</code> must never be
            deleted — the reference either blocks it or, worse, erases the record of why a ticket
            moved. Deactivating hides it from every picker while everything already using it stays
            readable.
          </p>
          {def.usageFilter ? (
            <p className="mt-1">
              The counts on each row are over <strong>all projects</strong> and come from{' '}
              <code>GET /tickets?{def.usageFilter}=…</code>'s own total.
            </p>
          ) : null}
        </Notice>
      </div>

      {rows.isLoading ? (
        <div role="status" aria-label={`Loading ${def.title}`} className="space-y-2 px-3 sm:px-4">
          {Array.from({ length: 6 }, (_, index) => (
            <SkeletonBar key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : null}

      {!rows.isLoading && rows.data && shown.length === 0 ? (
        <EmptyState
          title={`No ${def.title.toLowerCase()} are defined`}
          detail="The request succeeded and returned none — an empty list, not a failure."
        />
      ) : null}

      {shown.length > 0 ? (
        <>
          {pending && hasPendingOrder(serverRows, pending) ? (
            <div className="mx-3 mb-2 flex flex-wrap items-center gap-2 rounded border border-[color:var(--color-accent)] px-3 py-2 text-sm sm:mx-4">
              <span className="flex-1">
                Order changed — {orderChanges(serverRows, pending).length} row(s) to save.
              </span>
              <button
                type="button"
                disabled={savingOrder || !editable}
                title={disabledReason(gate)}
                onClick={() => void saveOrder()}
                className="h-8 rounded bg-[color:var(--color-accent)] px-3 text-xs font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
              >
                {savingOrder ? 'Saving…' : 'Save order'}
              </button>
              <button
                type="button"
                onClick={() => setPending(null)}
                className="h-8 rounded border border-[color:var(--color-line)] px-3 text-xs"
              >
                Discard
              </button>
            </div>
          ) : null}

          <ul className="border-y border-[color:var(--color-line)]">
            {shown.map((row, index) => (
              <VocabRowItem
                key={row.code}
                row={row}
                def={def}
                gate={gate}
                index={index}
                count={shown.length}
                usage={usageFor(row)}
                busy={busyCode === row.code}
                error={rowErrors[row.code] ?? null}
                onSave={(patch: UpdateVocabBody) =>
                  void runWrite(row.code, () => updateVocabRow(collection, row.code, patch))
                }
                onToggleActive={() =>
                  void runWrite(row.code, () =>
                    updateVocabRow(collection, row.code, { is_active: !row.is_active }),
                  )
                }
                onMove={(delta) => setPending(moveCode(shown, row.code, delta))}
                onDragStart={() => setDragCode(row.code)}
                onDragOver={(event) => {
                  if (dragCode) event.preventDefault();
                }}
                onDrop={() => {
                  if (!dragCode || dragCode === row.code) return;
                  const from = shown.findIndex((entry) => entry.code === dragCode);
                  setPending(moveCode(shown, dragCode, index - from));
                  setDragCode(null);
                }}
              />
            ))}
          </ul>
        </>
      ) : null}

      <AddRowForm
        def={def}
        gate={gate}
        lanes={def.name === 'statuses' ? lanes : []}
        busy={busyCode === NEW_ROW}
        error={addError}
        onCreate={(body: CreateVocabBody) => {
          setAddError(null);
          void (async () => {
            setBusyCode(NEW_ROW);
            try {
              await createVocabRow(collection, body);
              rows.reload();
              onCollectionChanged();
            } catch (err) {
              const refusal = refusalReason(err);
              setAddError(
                refusal ??
                  (writeRouteMissing(err)
                    ? 'The API answered "no such route" — POST /api/v1/settings/{collection} is not deployed.'
                    : toApiError(err, `/settings/${collection}`).message),
              );
            } finally {
              setBusyCode(null);
            }
          })();
        }}
      />
    </section>
  );
}

/** A sentinel busy key for the add form — it has no code of its own until it succeeds. */
const NEW_ROW = ' new';
